/**
 * Story 033 — Zustand store for defect-catalogue state.
 *
 * Single source of truth for:
 *   - The list of available catalogues for this tenant (`availableCatalogues`).
 *   - The currently-active catalogue id (set externally by Story 047).
 *   - Per-catalogue entries (`entries[catalogueId]`).
 *   - Selectors: `getEntryById`, `bySearch`, `currentForBlade`.
 *   - Per-(tenant, user) favourite entry ids (`favouriteCatalogueEntries`).
 *
 * Consumers: Stories 020 (popover), 023 (drawer), 030 (auto-group radius),
 * 047 (per-DA picker). All read from one store — there is no parallel
 * fetch, no parallel cache. AC4.
 */
import { create } from "zustand";
import type { DefectCatalogue, DefectCatalogueEntry } from "@aeriosiq/types";

export interface CataloguesStoreState {
  /** All catalogues the tenant owns (or `[globalDefault]` per AC6). */
  availableCatalogues: DefectCatalogue[];
  /** Selected catalogue id — set by Story 047, read by 020 / 023 / 030. */
  activeCatalogueId: string | null;
  /** entries keyed by catalogueId. */
  entries: Record<string, DefectCatalogueEntry[]>;
  /** Per-tenant favourite entryIds keyed by catalogueId. */
  favouriteCatalogueEntries: Record<string, string[]>;

  setAvailableCatalogues: (items: DefectCatalogue[]) => void;
  setActiveCatalogueId: (id: string | null) => void;
  setEntries: (catalogueId: string, entries: DefectCatalogueEntry[]) => void;
  setFavourites: (favouriteCatalogueEntries: Record<string, string[]>) => void;
  reset: () => void;

  // ---------- Selectors (cheap; called from render bodies) ----------
  /** Find an entry across all loaded catalogues by entryId. */
  getEntryById: (entryId: string) => DefectCatalogueEntry | null;
  /** Case-insensitive substring match against entry name OR description
   *  (en bucket only for MVP — synonym matching is Q1 in Story 033). */
  bySearch: (query: string) => DefectCatalogueEntry[];
  /**
   * Entries already used on (`bladeId`, `side`) in the active inspection.
   * The store doesn't own the active inspection's defects — the caller
   * passes them in. This selector lifts the cross-referencing so the
   * Catalogue popover (Story 020) doesn't need to re-implement the
   * filter logic. AC9 (deferred — depends on Story 047's picker).
   */
  currentForBlade: (
    bladeId: string,
    side: string,
    defects: readonly DefectLike[],
  ) => DefectCatalogueEntry[];
}

/** Minimal defect shape `currentForBlade` needs — keeps the store free
 *  of the full @aeriosiq/types `Defect` import. */
export interface DefectLike {
  catalogueEntryId?: string | null;
  bladeId?: string | null;
  side?: string | null;
}

const INITIAL_STATE: Pick<
  CataloguesStoreState,
  | "availableCatalogues"
  | "activeCatalogueId"
  | "entries"
  | "favouriteCatalogueEntries"
> = {
  availableCatalogues: [],
  activeCatalogueId: null,
  entries: {},
  favouriteCatalogueEntries: {},
};

export const useCataloguesStore = create<CataloguesStoreState>((set, get) => ({
  ...INITIAL_STATE,

  setAvailableCatalogues: (items) => {
    set({ availableCatalogues: items });
  },

  setActiveCatalogueId: (id) => {
    set({ activeCatalogueId: id });
  },

  setEntries: (catalogueId, entries) => {
    set((s) => ({ entries: { ...s.entries, [catalogueId]: entries } }));
  },

  setFavourites: (favouriteCatalogueEntries) => {
    set({ favouriteCatalogueEntries });
  },

  reset: () => {
    set({ ...INITIAL_STATE });
  },

  getEntryById: (entryId) => {
    const all = get().entries;
    for (const list of Object.values(all)) {
      const hit = list.find((e) => e.entryId === entryId);
      if (hit) return hit;
    }
    return null;
  },

  bySearch: (query) => {
    const q = query.trim().toLowerCase();
    if (q.length === 0) return [];
    const { activeCatalogueId, entries } = get();
    // Active catalogue only (AC8 — "within the currently-active catalogue").
    const list = activeCatalogueId ? (entries[activeCatalogueId] ?? []) : [];
    return list.filter((e) => {
      const name = e.name.en.toLowerCase();
      // `description` is deprecated (7.10.0 / DEFCAT-R2-08, always null on the
      // wire) — `defaultDescription` is its replacement.
      const desc = e.defaultDescription?.en.toLowerCase() ?? "";
      return name.includes(q) || desc.includes(q);
    });
  },

  currentForBlade: (bladeId, side, defects) => {
    // Defects on the same (blade, side) — collect distinct catalogueEntryIds.
    const ids = new Set<string>();
    for (const d of defects) {
      if (d.bladeId === bladeId && d.side === side && d.catalogueEntryId) {
        ids.add(d.catalogueEntryId);
      }
    }
    if (ids.size === 0) return [];
    const out: DefectCatalogueEntry[] = [];
    for (const id of ids) {
      const e = get().getEntryById(id);
      if (e) out.push(e);
    }
    return out;
  },
}));
